import React from 'react';
import { IFlightDetail } from '../../interfaces/flightDetails';
import { IFlightBookingDetails } from '../../interfaces/flightBookingDetails';
import { translate } from '../../i18n';
import ReusableButton from './Button/Button';
import CheckIcon from '../../assets/CheckIcon';
import CrossIcon from '../../assets/CrossIcon';
import useBgColorStore from '../../store/bgColor';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';

export interface ISelectedFlightsData {
  bookingProductTripId: number;
  bookingId: number | string;
  paxNo: number | string;
  allocNo: number | string;
  zoneId: number;
  allocations: {
    operationBalloonFlightId: number;
    oldOperationBalloonFlightId: number | null;
    splitPax: number;

    splitMaleNo: number;
    splitFemaleNo: number;
    splitChildNo: number;
    isDeleted?: boolean;

    maleWeight: string | number;
    femaleWeight: string | number;
    childWeight: string | number;
  }[];
}

interface BookingDetailsCardProps {
  bookingId: number;
  bookingProductTripId: number;
  operationBalloonFlightId: number;
  pickup: string;
  dropoff: string;
  id: number;
  earlyReturn: boolean;
  comment: string;
  tour: string;
  agent: string;
  allocated: number;
  flight: string;
  pax: number;
  male: number;
  female: number;
  child: number;
  maleWeight: string;
  femaleWeight: string;
  childWeight: string;
  zoneId: number;
  flights: IFlightDetail[];
  selectedFlight: {
    [key: string]: ISelectedFlightsData;
  };
  handleCheckboxChange: (
    bookingId: number,
    operationBalloonFlightId: number,
    bookingProductTripId: number,
    paxNo: number,
    maleNo: number,
    femaleNo: number,
    childNo: number,
    maleWeight: string | number,
    femaleWeight: string | number,
    childWeight: string | number,
    zoneId: number,
    oldOperationBalloonFlightId?: number | null,
  ) => void;
  handlePaxCountChange: (
    bookingId: string | number,
    bookingProductTripId: number | string,
    operationBalloonFlightId: number,
    field: 'splitMaleNo' | 'splitFemaleNo' | 'splitChildNo',
    value: number | '',
    zoneId: number,
  ) => void;
  cancelEditMode: (bookingId: number, bookingProductTripId: number) => void;
  allocatedFlight: (booking: IFlightBookingDetails) => void;
  editModeMap: { [key: string]: boolean };
  bookings: IFlightBookingDetails;
  isLoading: boolean;
}

const FlightBookingDetailsCard: React.FC<BookingDetailsCardProps> = ({
  bookingId,
  bookingProductTripId,
  operationBalloonFlightId,
  pickup,
  dropoff,
  id,
  earlyReturn,
  comment,
  tour,
  agent,
  allocated,
  flight,
  pax,
  male,
  female,
  child,
  maleWeight,
  femaleWeight,
  childWeight,
  flights,
  zoneId,
  selectedFlight,
  handleCheckboxChange,
  handlePaxCountChange,
  allocatedFlight,
  cancelEditMode,
  editModeMap,
  bookings,
  isLoading,
}) => {
  const bookingIdString = String(bookingId);
  const zoneIdStr = String(zoneId);
  const bookingProductTripIdStr = String(bookingProductTripId);
  const combinedKey = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;
  const { bgColor, updateBgColor } = useBgColorStore();

  const paxAllocatedCount =
    selectedFlight[combinedKey] &&
    selectedFlight[combinedKey]?.allocations?.filter((a) => !a.isDeleted)
      ?.length > 0
      ? selectedFlight[combinedKey]?.allocNo
      : 0;

  return (
    <div className="">
      <div className="grid grid-cols-2 gap-y-2 text-sm text-textColorGray text-left">
        <div>
          <p className="font-medium">{translate('allocation.Pickup')}</p>
          <p className="max-w-[140px]">{pickup}</p>
        </div>
        <div>
          <p className="font-medium">{translate('allocation.dropOff')}</p>
          <p>{dropoff}</p>
        </div>
        <div>
          <p className="font-medium">{translate('allocation.id')}:</p>
          <p>{id}</p>
        </div>
        <div>
          <p className="font-medium">
            {translate('flightAllocation.earlyReturn')}:
          </p>
          <p>
            {earlyReturn === true
              ? translate('common.yes')
              : translate('common.no')}
          </p>
        </div>
        <div className="col-span-2">
          <p className="font-medium">{translate('allocation.comment')}:</p>
          <p>{comment}</p>
        </div>
        <div>
          <p className="font-medium">{translate('allocation.tour')}:</p>
          <p>{tour}</p>
        </div>
        <div>
          <p className="font-medium">{translate('allocation.agent')}:</p>
          <p>{agent}</p>
        </div>
        <div>
          <p className="font-medium">
            {translate('flightAllocation.allocated')}:
          </p>
          <p className="text-text-primary">{paxAllocatedCount}</p>
        </div>
        <div>
          <p className="font-medium">{translate('allocation.flight')}:</p>
          <p className="text-text-primary">{flight}</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-4 text-left">
        <div
          className={`${bgColor} "rounded-md p-2 flex flex-col items-center" `}
        >
          <p className="font-medium text-textColorGray">
            {translate('allocation.pax')}
          </p>
          {/* <p>{pax}</p> TODO: I will remove in this next PR */}
          <p
            className={`${paxAllocatedCount === 0 ? 'text-text-red' : 'text-text-green'}`}
          >
            {paxAllocatedCount}
          </p>
        </div>
        <div className={`${bgColor} rounded-md p-2 flex flex-col items-center`}>
          <p className="font-medium text-textColorGray">
            {translate('flightAllocation.male')}
          </p>
          <p className="text-text-primary">{male}</p>
        </div>
        <div className={`${bgColor} rounded-md p-2 flex flex-col items-center`}>
          <p className="font-medium text-textColorGray">
            {translate('flightAllocation.female')}
          </p>
          <p className="text-text-primary">{female}</p>
        </div>
        <div className={`${bgColor} rounded-md p-2 flex flex-col items-center`}>
          <p className="font-medium text-textColorGray">
            {translate('flightAllocation.child')}
          </p>
          <p className="text-text-primary">{child}</p>
        </div>
      </div>

      <div className={`${bgColor} rounded-md p-3 mt-4 text-center`}>
        <h3 className="text-start text-textColorGray">
          {translate('flightAllocation.weight')}
        </h3>
        <div className="grid grid-cols-3">
          <div>
            <p className="font-medium text-textColorGray">
              {translate('flightAllocation.male')}
            </p>
            <p className="text-text-primary">{maleWeight}</p>
          </div>
          <div>
            <p className="font-medium text-textColorGray">
              {translate('flightAllocation.female')}
            </p>
            <p className="text-text-primary">{femaleWeight}</p>
          </div>
          <div>
            <p className="font-medium text-textColorGray">
              {translate('flightAllocation.child')}
            </p>
            <p className="text-text-primary">{childWeight}</p>
          </div>
        </div>
      </div>

      <div className="space-y-2 mt-4">
        {flights.map((flight, index) => (
          <label
            key={index}
            className="flex items-center space-x-2 cursor-pointer"
          >
            <input
              disabled={!editModeMap[`${bookingId}_${bookingProductTripId}`]}
              type="checkbox"
              className="w-5 h-5 text-[white] border-gray-300 rounded focus:ring-blue-500"
              checked={
                // First check if this bus is selected in our state
                selectedFlight[combinedKey]?.allocations.some(
                  (a) =>
                    String(a.operationBalloonFlightId) ===
                      String(flight.operationBalloonFlightId) && !a.isDeleted,
                ) ||
                // Or if it's in the original allocation but not yet in our state
                (!selectedFlight[combinedKey] &&
                  bookings?.flightAllocation?.some(
                    (allocation) =>
                      String(allocation.operationBalloonFlightId) ===
                      String(flight.operationBalloonFlightId),
                  ))
              }
              onChange={() => {
                // Find the existing allocation if any
                const existingAllocation = bookings?.flightAllocation?.find(
                  (allocation) =>
                    String(allocation.operationBalloonFlightId) ===
                    String(flight.operationBalloonFlightId),
                );
                handleCheckboxChange(
                  bookingId,
                  flight?.operationBalloonFlightId,
                  bookingProductTripId,
                  pax,
                  male,
                  female,
                  child,
                  maleWeight,
                  femaleWeight,
                  childWeight,
                  zoneId,
                  existingAllocation?.operationBalloonFlightId || null,
                );
              }}
            />
            <span className="text-textColor">
              {flight.formattedBalloonName}
            </span>
            {editModeMap[`${bookingId}_${bookingProductTripId}`] &&
              selectedFlight[combinedKey]?.allocations?.filter(
                (a) => !a.isDeleted,
              ).length > 1 &&
              selectedFlight[combinedKey]?.allocations?.some(
                (a) =>
                  String(a.operationBalloonFlightId) ===
                    String(flight.operationBalloonFlightId) && !a.isDeleted,
              ) && (
                <div className="flex items-baseline">
                  {/* Male input */}
                  <label className="text-sm">M: </label>
                  <input
                    type="number"
                    placeholder="Male"
                    className="w-16 p-1 border rounded mr-[1px] text-black"
                    value={
                      selectedFlight[combinedKey]?.allocations.find(
                        (allocation) =>
                          String(allocation.operationBalloonFlightId) ===
                            String(flight?.operationBalloonFlightId) &&
                          !allocation.isDeleted,
                      )?.splitMaleNo ?? ''
                    }
                    onChange={(e) => {
                      const inputValue =
                        e.target.value === '' ? '' : Number(e.target.value);

                      handlePaxCountChange(
                        bookingId,
                        bookingProductTripId,
                        flight?.operationBalloonFlightId,
                        'splitMaleNo',
                        inputValue,
                        zoneId,
                      );
                    }}
                    max={male}
                  />

                  {/* Female input */}
                  <label className="text-sm">F: </label>
                  <input
                    type="number"
                    placeholder="Female"
                    className="w-16 p-1 border rounded mr-[1px] text-black"
                    value={
                      selectedFlight[combinedKey]?.allocations.find(
                        (allocation) =>
                          String(allocation.operationBalloonFlightId) ===
                            String(flight?.operationBalloonFlightId) &&
                          !allocation.isDeleted,
                      )?.splitFemaleNo ?? ''
                    }
                    onChange={(e) => {
                      const inputValue =
                        e.target.value === '' ? '' : Number(e.target.value);

                      handlePaxCountChange(
                        bookingId,
                        bookingProductTripId,
                        flight?.operationBalloonFlightId,
                        'splitFemaleNo',
                        inputValue,
                        zoneId,
                      );
                    }}
                    max={female}
                  />

                  {/* Child input */}
                  <label className="text-sm">Ch: </label>
                  <input
                    type="number"
                    placeholder="Child"
                    className="w-16 p-1 border rounded  mr-[1px] text-black"
                    value={
                      selectedFlight[combinedKey]?.allocations.find(
                        (allocation) =>
                          String(allocation.operationBalloonFlightId) ===
                            String(flight?.operationBalloonFlightId) &&
                          !allocation.isDeleted,
                      )?.splitChildNo ?? ''
                    }
                    onChange={(e) => {
                      const inputValue =
                        e.target.value === '' ? '' : Number(e.target.value);

                      handlePaxCountChange(
                        bookingId,
                        bookingProductTripId,
                        flight?.operationBalloonFlightId,
                        'splitChildNo',
                        inputValue,
                        zoneId,
                      );
                    }}
                    max={child}
                  />
                </div>
              )}
          </label>
        ))}
      </div>

      {editModeMap[`${bookingId}_${bookingProductTripId}`] && (
        <div className="flex space-x-4 mt-4">
          <ReusableButton
            variant="secondary"
            children={translate('common.cancel')}
            icon={
              <div>
                <div className="block dark:hidden">
                  <CrossIcon />
                </div>
                <div className="hidden dark:block">
                  <CrossIconDarkForm />
                </div>
              </div>
            }
            className="w-full bg-button-cancelButtonBgColor text-text-cancelButtonTextColor"
            onClick={() => cancelEditMode(bookingId, bookingProductTripId)}
          />

          <ReusableButton
            icon={<CheckIcon />}
            children={translate('common.confirm')}
            variant="primary"
            className="w-full bg-background-backgroundColor"
            onClick={() => allocatedFlight(bookings)}
            isLoader={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default FlightBookingDetailsCard;
